import { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ProdiSelect from "@/components/ProdiSelect";
import { UsersRound, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import PasswordRequirements from "@/components/PasswordRequirements";
import { isPasswordValid } from "@/lib/passwordRules";

const normName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function BulkCreateAdminDialog({ open, onOpenChange, prodiList, existingUsers, onDone }) {
    const [step, setStep] = useState(1); // 1 paste, 2 preview, 3 result
    const [text, setText] = useState("");
    const [password, setPassword] = useState("");
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => {
        setStep(1); setText(""); setPassword(""); setRows([]); setResult(null);
    };
    const close = (v) => { if (!v) reset(); onOpenChange(v); };

    const existingEmails = useMemo(
        () => new Set((existingUsers || []).map((u) => (u.email || "").toLowerCase())),
        [existingUsers],
    );

    const matchProdi = (namaProdi) => {
        const n = normName(namaProdi);
        if (!n) return "";
        const exact = prodiList.find((p) => normName(p.nama_prodi) === n);
        if (exact) return exact.id;
        const partial = prodiList.find(
            (p) => normName(p.nama_prodi).includes(n) || n.includes(normName(p.nama_prodi)),
        );
        return partial ? partial.id : "";
    };

    const doPreview = () => {
        const lines = text.split(/\r?\n/).map((l) => l.replace(/\s+$/, "")).filter((l) => l.trim());
        if (lines.length === 0) return toast.error("Tempel dulu data akun di kotak teks");
        const parsed = lines.map((line, idx) => {
            let parts = line.includes("\t") ? line.split("\t") : line.split(",");
            parts = parts.map((p) => p.trim());
            const [nama = "", email = "", namaProdi = ""] = parts;
            return { idx, nama, email: email.toLowerCase(), namaProdi, prodi_id: matchProdi(namaProdi) };
        });
        setRows(parsed);
        setStep(2);
    };

    const setRowProdi = (idx, prodi_id) =>
        setRows((prev) => prev.map((r) => (r.idx === idx ? { ...r, prodi_id } : r)));

    const rowErrors = (r) => {
        const errs = [];
        if (!r.nama.trim()) errs.push("Nama kosong");
        if (!EMAIL_RE.test(r.email)) {
            errs.push("Email tidak valid");
        } else {
            if (existingEmails.has(r.email)) errs.push("Email sudah terdaftar");
            if (rows.filter((x) => x.email && x.email === r.email).length > 1) errs.push("Email duplikat di batch");
        }
        if (!r.prodi_id) errs.push(`Prodi tidak ditemukan: "${r.namaProdi || "(kosong)"}"`);
        return errs;
    };

    const evaluated = useMemo(
        () => rows.map((r) => ({ ...r, errors: rowErrors(r) })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, existingEmails],
    );
    const validRows = evaluated.filter((r) => r.errors.length === 0);

    const execute = async () => {
        if (!isPasswordValid(password)) return toast.error("Kata sandi default belum memenuhi semua syarat");
        if (validRows.length === 0) return toast.error("Tidak ada baris valid");
        setBusy(true);
        try {
            const { data } = await api.post("/users/bulk-create", {
                accounts: validRows.map((r) => ({ nama: r.nama.trim(), email: r.email, prodi_id: r.prodi_id })),
                password,
            });
            setResult(data);
            setStep(3);
            toast.success(`${data.created} akun berhasil dibuat`);
            onDone && onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UsersRound className="w-5 h-5 text-teal-600" /> Tambah Banyak Akun Admin Prodi
                    </DialogTitle>
                </DialogHeader>

                {step === 1 && (
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">
                                Tempel data (dari Excel / Google Sheets)
                            </Label>
                            <p className="text-[11px] text-slate-500 mt-0.5 mb-1.5">
                                Satu akun per baris, urutan kolom: <b>Nama</b> — <b>Email</b> — <b>Nama Prodi</b>.
                                Pilih 3 kolom di spreadsheet lalu salin-tempel (otomatis terpisah dengan TAB).
                                Pemisah koma juga diterima.
                            </p>
                            <Textarea
                                rows={10}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder={"dr. Andi Wijaya\tandi@rscm.co.id\tAnestesiologi & Terapi Intensif\ndr. Budi Santoso\tbudi@rscm.co.id\tBedah"}
                                className="font-mono text-xs"
                                data-testid="bulk-admin-textarea"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">
                                Kata Sandi Default untuk Semua Akun
                            </Label>
                            <Input
                                type="text"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="mis. GantiSaya2026!"
                                data-testid="bulk-admin-password"
                            />
                            <PasswordRequirements password={password} />
                            <p className="text-[11px] text-amber-600 mt-1">
                                Kata sandi yang sama dipakai semua akun di batch ini. Setiap user wajib menggantinya saat login pertama.
                            </p>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="p-3 rounded-lg bg-slate-100 border border-slate-200">
                                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Total baris</div>
                                <div className="text-2xl font-extrabold text-slate-900">{evaluated.length}</div>
                            </div>
                            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                                <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Valid</div>
                                <div className="text-2xl font-extrabold text-emerald-700" data-testid="bulk-admin-valid">{validRows.length}</div>
                            </div>
                            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                                <div className="text-[11px] uppercase tracking-widest text-rose-700 font-semibold">Error</div>
                                <div className="text-2xl font-extrabold text-rose-700">{evaluated.length - validRows.length}</div>
                            </div>
                        </div>

                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <div className="max-h-[46vh] overflow-auto">
                                <table className="w-full compact">
                                    <thead className="bg-slate-100 sticky top-0">
                                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                            <th className="w-8"></th>
                                            <th>Nama</th><th>Email</th><th>Nama Prodi (paste)</th><th>Prodi yang Cocok</th><th>Error</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {evaluated.map((r) => (
                                            <tr key={r.idx} className={`border-t border-slate-200 ${r.errors.length ? "bg-rose-50/40" : ""}`}>
                                                <td className="pl-2">
                                                    {r.errors.length === 0
                                                        ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                                        : <AlertCircle className="w-4 h-4 text-rose-600" />}
                                                </td>
                                                <td className="font-semibold text-slate-800">{r.nama || <em className="text-slate-400">(kosong)</em>}</td>
                                                <td className="font-mono text-[11px]">{r.email || "-"}</td>
                                                <td className="text-xs text-slate-600">{r.namaProdi || "-"}</td>
                                                <td>
                                                    <ProdiSelect
                                                        prodi={prodiList}
                                                        value={r.prodi_id || ""}
                                                        onValueChange={(v) => setRowProdi(r.idx, v)}
                                                        placeholder="— pilih manual —"
                                                        className="h-8 w-56 text-xs"
                                                    />
                                                </td>
                                                <td className="text-[11px] text-rose-700">
                                                    {r.errors.length > 0 && (
                                                        <ul className="list-disc pl-4 space-y-0.5">
                                                            {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                                                        </ul>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <p className="text-[11px] text-slate-500">
                            Semua akun dibuat sebagai <b>Admin Prodi</b> dengan kata sandi default yang Anda isi. Baris error dilewati.
                        </p>
                    </div>
                )}

                {step === 3 && result && (
                    <div className="space-y-4">
                        <div className="flex gap-3">
                            <div className="flex-1 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                                <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">Berhasil dibuat</div>
                                <div className="text-2xl font-extrabold text-emerald-700" data-testid="bulk-admin-created">{result.created}</div>
                            </div>
                            <div className="flex-1 rounded-lg bg-rose-50 border border-rose-200 p-3">
                                <div className="text-[11px] uppercase tracking-widest text-rose-700 font-semibold">Gagal</div>
                                <div className="text-2xl font-extrabold text-rose-700">{result.failed.length}</div>
                            </div>
                        </div>
                        {result.failed.length > 0 && (
                            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                                <table className="w-full compact">
                                    <thead className="bg-slate-100 sticky top-0">
                                        <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                            <th>Nama</th><th>Email</th><th>Alasan</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.failed.map((f, i) => (
                                            <tr key={i} className="border-t border-slate-200">
                                                <td className="font-semibold text-slate-800">{f.nama || "-"}</td>
                                                <td className="font-mono text-[11px]">{f.email || "-"}</td>
                                                <td className="text-xs text-rose-700">{f.error}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {step === 1 && (
                        <>
                            <Button variant="outline" onClick={() => close(false)}>Batal</Button>
                            <Button className="bg-[#00B9AD] hover:bg-[#60C0D0]" onClick={doPreview} data-testid="bulk-admin-preview">
                                Preview
                            </Button>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <Button variant="outline" onClick={() => setStep(1)}>
                                <ArrowLeft className="w-4 h-4 mr-2" /> Kembali
                            </Button>
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-700"
                                disabled={busy || validRows.length === 0}
                                onClick={execute}
                                data-testid="bulk-admin-execute"
                            >
                                {busy ? "Memproses..." : `Buat Semua Akun (${validRows.length} valid)`}
                            </Button>
                        </>
                    )}
                    {step === 3 && (
                        <>
                            <Button variant="outline" onClick={reset}>Buat Batch Lagi</Button>
                            <Button className="bg-[#00B9AD] hover:bg-[#60C0D0]" onClick={() => close(false)}>Selesai</Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
